// frontend/src/components/staff/ScheduleBuilderToolbar.js
//
// Workforce — SCHED-SLICE1-E toolbar (week navigator + publish CTA).
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §3.5 +
// §3.7 (ADMIN MODE chrome bar) + Scenario 9.
//
// Owns:
//   - ADMIN MODE chrome bar (gold tint, persistent, aria-label per spec)
//   - Week navigator: prev / Today / next chevrons + clickable week label
//     (opens a native date input to jump to any week)
//   - Publish CTA (label + enable state derived from `computePublishCtaState`)
//   - "View audit log" link to /staff/schedule/audit?weekStart=YYYY-MM-DD
//     (SCHED-SLICE1-F page)
//
// Keyboard shortcuts on toolbar focus: Left/Right arrows = prev/next week;
// "T" = today (visual spec §3.5).

import React from "react";
import "./ScheduleBuilderToolbar.css";

const ScheduleBuilderToolbar = ({
  weekStartYmd,
  weekRangeLabel,
  publishCta,
  onPrevWeek,
  onNextWeek,
  onToday,
  onWeekStartChange,
  onPublishClick,
  auditLinkHref,
  staffFilterLabel,
  onClearStaffFilter,
}) => {
  const toolbarRef = React.useRef(null);

  const handleKey = (e) => {
    if (e.target && e.target.tagName === "INPUT") return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      onPrevWeek();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      onNextWeek();
    } else if (e.key === "t" || e.key === "T") {
      e.preventDefault();
      onToday();
    }
  };

  return (
    <>
      <div
        className="schedule-builder-toolbar__admin-bar"
        role="note"
        aria-label="Admin schedule editor mode"
        data-testid="schedule-builder-admin-bar"
      >
        <span className="schedule-builder-toolbar__admin-bar-text">
          ADMIN MODE
        </span>
      </div>

      <div
        ref={toolbarRef}
        className="schedule-builder-toolbar"
        onKeyDown={handleKey}
        data-testid="schedule-builder-toolbar"
      >
        <div className="schedule-builder-toolbar__nav">
          <button
            type="button"
            className="schedule-builder-toolbar__chev"
            onClick={onPrevWeek}
            aria-label="Previous week"
            data-testid="schedule-builder-toolbar-prev"
          >
            &lt;
          </button>
          <label className="schedule-builder-toolbar__week-label">
            <input
              type="date"
              className="schedule-builder-toolbar__date-input"
              value={weekStartYmd || ""}
              onChange={(e) => onWeekStartChange && onWeekStartChange(e.target.value)}
              aria-label="Jump to week"
              data-testid="schedule-builder-toolbar-date-input"
            />
            <span className="schedule-builder-toolbar__week-text">
              {weekRangeLabel}
            </span>
          </label>
          <button
            type="button"
            className="schedule-builder-toolbar__today"
            onClick={onToday}
            data-testid="schedule-builder-toolbar-today"
          >
            Today
          </button>
          <button
            type="button"
            className="schedule-builder-toolbar__chev"
            onClick={onNextWeek}
            aria-label="Next week"
            data-testid="schedule-builder-toolbar-next"
          >
            &gt;
          </button>
        </div>

        <div className="schedule-builder-toolbar__actions">
          {staffFilterLabel && (
            <span
              className="schedule-builder-toolbar__filter-chip"
              data-testid="schedule-builder-toolbar-filter-chip"
            >
              Filtered to: {staffFilterLabel}
              <button
                type="button"
                onClick={onClearStaffFilter}
                aria-label="Clear staff filter"
                className="schedule-builder-toolbar__filter-chip-clear"
              >
                &times;
              </button>
            </span>
          )}
          {auditLinkHref && (
            <a
              className="schedule-builder-toolbar__audit-link"
              href={auditLinkHref}
              data-testid="schedule-builder-toolbar-audit-link"
            >
              View audit log
            </a>
          )}
          <button
            type="button"
            className="schedule-builder-toolbar__publish"
            onClick={onPublishClick}
            disabled={!publishCta.enabled}
            data-testid="schedule-builder-toolbar-publish"
            aria-label={
              publishCta.enabled
                ? `Publish week of ${weekRangeLabel}`
                : "No draft shifts to publish"
            }
          >
            {publishCta.label}
          </button>
        </div>
      </div>
    </>
  );
};

export default ScheduleBuilderToolbar;
